import { ORGANIZATIONS_KEY, organizationsAtom } from '@/atoms/organizations.atom';
import { buildOrgUrl, getOrgSlugFromHostname, getPreferredOrgSlug, isAppDomain } from '@/core/domain';
import { execute } from '@/graphql/execute';
import { MyOrganizationsQuery, UserOrganizationRole } from '@/graphql/graphql';
import { myOrganizationsQuery } from '@/queries/organization.query';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const roleToLevel: Record<UserOrganizationRole, number> = {
  ADMIN: 3,
  EDITOR: 2,
  MEMBER: 1,
  BILLING_MANAGER: 0,
};

const hasOrganizationRole = (actualRole: UserOrganizationRole, requiredRole: UserOrganizationRole) => {
  if (actualRole === 'ADMIN') return true;
  if (requiredRole === 'BILLING_MANAGER') return actualRole === 'BILLING_MANAGER';
  if (actualRole === 'BILLING_MANAGER') return false;
  return roleToLevel[actualRole] >= roleToLevel[requiredRole];
};

// One shared query for the ~20 components that call useOrganizations(): React
// Query dedupes concurrent fetches and serves later mounts from cache, so a
// viewer page issues one MyOrganizations request instead of one per layer row.
// Each request costs the server a storage SUM + ephemeral SUM + seat COUNT per
// organization, so this matters for the API bill as much as for load time.
export const myOrganizationsQueryOptions = () =>
  queryOptions({
    queryKey: ['myOrganizations'],
    queryFn: async () => {
      const data = await execute(myOrganizationsQuery);
      const organizations = (data.organizations || []) as MyOrganizationsQuery['organizations'];
      localStorage.setItem(ORGANIZATIONS_KEY, JSON.stringify(organizations));
      return organizations;
    },
    staleTime: 5 * 60 * 1000,
  });

export const useOrganization = () => {
  const [organizations] = useAtom(organizationsAtom);
  const slug = getOrgSlugFromHostname();
  return organizations.find((organization) => organization.slug === slug);
};

export const useOrganizations = () => {
  const { pathname } = useLocation();
  const slug = getOrgSlugFromHostname();
  const [organizations, setOrganizations] = useAtom(organizationsAtom);
  const [loaded, setLoaded] = useState(false);
  const query = useQuery(myOrganizationsQueryOptions());

  const navigate = useNavigate();

  // Push fresh data into the atom and flip `loaded` in the same effect so the
  // redirect logic below sees both together (as the old imperative loader did).
  useEffect(() => {
    if (!query.data) return;
    setOrganizations(query.data);
    setLoaded(true);
  }, [query.data]);

  useEffect(() => {
    if (!loaded) return;
    if (pathname.startsWith('/shared')) return;
    if (organizations.length === 0) {
      navigate('/auth/onboarding/org');
      return;
    }
    // On app domain with no org slug, prefer the org that initiated login.
    if (isAppDomain() && !pathname.startsWith('/auth')) {
      const preferredSlug = getPreferredOrgSlug(
        organizations.map((organization) => organization.slug).filter((slug): slug is string => !!slug),
      );
      window.location.href = buildOrgUrl(preferredSlug || organizations[0].slug!);
    }
  }, [slug, organizations, loaded]);

  const _handleLoadOrganizations = async () => {
    await query.refetch();
  };

  const _handleOrganizationChange = (orgSlug: string) => {
    if (orgSlug === slug) return;
    window.location.href = buildOrgUrl(orgSlug);
  };

  const organization = useMemo(
    () => (slug ? organizations.find((organization) => organization.slug === slug) : undefined),
    [organizations, slug],
  );

  // Not a member of the org on this subdomain → move to one the user is in.
  // Only on a FRESH list: the cached one can predate this session (another
  // account was signed in on this page, or the user was added to the org
  // since), and bouncing on it sends a member of this org somewhere else.
  useEffect(() => {
    if (!slug || organization || !loaded || organizations.length === 0) return;
    let cancelled = false;
    // cancelRefetch: false — every useOrganizations() caller lands here at once;
    // share one request instead of each cancelling the previous one.
    query.refetch({ cancelRefetch: false }).then(({ data }) => {
      if (cancelled || !data?.length || data.some((organization) => organization.slug === slug)) return;
      const preferredSlug = getPreferredOrgSlug(
        data.map((organization) => organization.slug).filter((slug): slug is string => !!slug),
      );
      window.location.href = buildOrgUrl(preferredSlug || data[0].slug!);
    });
    return () => {
      cancelled = true;
    };
  }, [slug, organization, loaded, organizations]);

  const _checkOrganizationRole = (role: UserOrganizationRole) => {
    return hasOrganizationRole(organization?.role || 'MEMBER', role);
  };

  return {
    organizations,
    setOrganizations,
    refreshOrganizations: _handleLoadOrganizations,
    organizationChange: _handleOrganizationChange,
    hasRole: _checkOrganizationRole,
    organization,
  };
};
